import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch, useAuth } from "./auth";
import { applyConsent, conversion, pageView } from "./analytics";

export { conversion } from "./analytics";

const VID = "vl-visitor";
const SID = "vl-session";
const SID_AT = "vl-session-at";
const SESSION_GAP = 30 * 60 * 1000;
const CONSENT = "vl-consent";
export const CONSENT_EVT = "vl-consent-change";

const readCookie = (n) => (document.cookie.split("; ").find((c) => c.startsWith(n + "=")) || "").split("=")[1];
const newId = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

/* One id per browser: localStorage first, the first-party vl_vid cookie (set by the server) as the fallback when storage was cleared. */
export const visitorId = () => {
  let v = localStorage.getItem(VID) || readCookie("vl_vid");
  if (!/^v_[a-z0-9]{8,32}$/.test(v || "")) v = newId("v");
  if (localStorage.getItem(VID) !== v) localStorage.setItem(VID, v);
  return v;
};
const utmParams = () => {
  const out = {};
  for (const [k, v] of new URLSearchParams(window.location.search)) { if (k.startsWith("utm_")) out[k.slice(4)] = v; else if (["gclid", "fbclid", "msclkid"].includes(k)) out[k] = v; }
  return out;
};
let sessionReady = Promise.resolve();
const startSession = (sid) => { sessionReady = apiFetch("/api/visit/session", { method: "POST", body: JSON.stringify({ visitor_id: visitorId(), session_id: sid, landing: window.location.pathname + window.location.search, referrer: document.referrer, utm: utmParams(), lang: navigator.language, screen: `${window.screen.width}x${window.screen.height}`, tz: Intl.DateTimeFormat().resolvedOptions().timeZone, persona: localStorage.getItem("vl-site") === "business" ? localStorage.getItem("vl-biz-persona") : "applicant" }) }).catch(() => {}); return sessionReady; };
/* A session ends after 30 idle minutes; a new one posts the session beacon (location, device, referrer, campaign) once. */
export const sessionId = () => {
  const now = Date.now();
  let s = sessionStorage.getItem(SID);
  if (!/^s_[a-z0-9]{8,32}$/.test(s || "") || now - Number(sessionStorage.getItem(SID_AT) || 0) > SESSION_GAP) { s = newId("s"); sessionStorage.setItem(SID, s); startSession(s); }
  sessionStorage.setItem(SID_AT, String(now));
  return s;
};
export const getConsent = () => { try { return JSON.parse(localStorage.getItem(CONSENT)); } catch { return null; } };
export const setConsent = async (c) => {
  localStorage.setItem(CONSENT, JSON.stringify({ ...c, at: new Date().toISOString() }));
  window.dispatchEvent(new Event(CONSENT_EVT));
  applyConsent(c);
  await apiFetch("/api/visit/consent", { method: "POST", body: JSON.stringify({ visitor_id: visitorId(), analytics: !!c.analytics, marketing: !!c.marketing, persona: localStorage.getItem("vl-site") === "business" ? localStorage.getItem("vl-biz-persona") : "applicant", landing: window.location.pathname, referrer: document.referrer }) }).catch(() => {});
  flush(true);
};

/* First-party events go to our own backend for every visitor; the consent choice only switches the Google/Clarity tags in analytics.js. */
let queue = [];
let timer = null;
const flush = async (force = false) => {
  if (!queue.length) return;
  const sid = sessionId();
  if (!force) await sessionReady; // the session beacon writes the visitor row first, so counters are not lost to a race
  const batch = queue.splice(0, 50);
  if (!batch.length) return;
  try { await apiFetch("/api/visit/events", { method: "POST", body: JSON.stringify({ visitor_id: visitorId(), session_id: sid, events: batch }), keepalive: force }); } catch { /* drop */ }
};
export const track = (type, meta = {}) => {
  queue.push({ type, path: window.location.pathname + window.location.search, meta, at: new Date().toISOString() });
  clearTimeout(timer);
  timer = setTimeout(flush, 1500);
};
export const identify = () => apiFetch("/api/visit/identify", { method: "POST", body: JSON.stringify({ visitor_id: visitorId() }) }).catch(() => {});
/* Call from any form that collects an e-mail or phone: ties this browser's journey to the lead/contact behind that person. */
export const identifyLead = ({ email = "", name = "", phone = "", source = "form" }) => (email || phone ? apiFetch("/api/visit/identify-lead", { method: "POST", body: JSON.stringify({ visitor_id: visitorId(), session_id: sessionId(), email, name, phone, source }), keepalive: true }).catch(() => {}) : Promise.resolve());

export function useTracking() {
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  const last = useRef(null);
  useEffect(() => {
    if (pathname.startsWith("/ops") || pathname.startsWith("/marketing")) return;
    sessionId();
    track("page_view", { title: document.title.slice(0, 80) });
    if (last.current) pageView(pathname + search);
    last.current = pathname + search;
  }, [pathname, search]);
  useEffect(() => { if (user && user.user_id !== last.userId && !/^\/(ops|marketing)/.test(pathname)) { last.userId = user.user_id; identify(); } }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  // Every tel: / WhatsApp link on the public site counts as a call_click / whatsapp_click, wherever it sits.
  useEffect(() => {
    const onClick = (e) => {
      const p = window.location.pathname;
      if (p.startsWith("/ops") || p.startsWith("/marketing")) return;
      const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const where = a.dataset.testid || a.getAttribute("aria-label") || "";
      if (/^tel:/i.test(href)) conversion("call_click", { number: href.slice(4), location: where, page: p });
      else if (/wa\.me|whatsapp\.com|^whatsapp:/i.test(href)) conversion("whatsapp_click", { location: where, page: p });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  useEffect(() => {
    const onHide = () => flush(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => { document.removeEventListener("visibilitychange", onHide); window.removeEventListener("pagehide", onHide); };
  }, []);
}
